/**
 * How many things are open over the page, and who owns the page's scroll.
 *
 * Split out of `scrollLock.ts` because this is the part with a rule in it — and this project tests
 * rules in Node, with the DOM left to the render checks.
 *
 * The rule is nesting. A confirmation opens *from inside* an opened card (Remove sits in its action
 * row), so two locks are live at once; `enter`/`leave` answer "am I the one who takes it" and "am I
 * the one who gives it back", which a boolean cannot. With a flag, dismissing the confirmation frees
 * the page while the card is still open — the bug returning at the exact moment somebody is deciding
 * whether to destroy something.
 */

let held = 0

/** Take a lock. `true` when the page has to be frozen now, `false` when somebody already froze it. */
export function enter(): boolean {
  return ++held === 1
}

/** Give one back. `true` only when the last one goes, which is when the page is handed back. */
export function leave(): boolean {
  if (held === 0) {
    //: An unbalanced release — a double cleanup, a hot reload. Ignored rather than allowed to drive
    //: the count negative, where the **next** lock would silently do nothing: a page that stops
    //: freezing from the second dialog of a session onwards, which is unreproducible on a fresh load
    //: and therefore the worst shape this bug could take.
    return false
  }
  return --held === 0
}

/** For tests, and for reasoning about a leak rather than inferring one. */
export function depth(): number {
  return held
}
